import { jingleCollectionByIdQuery } from '@/entities/jingle-collections'
import {
  TrackUploadStatus,
  jingleByIdUploadStatus,
  jingleUpdate,
} from '@/entities/tracks'
import {
  type UploadFileWithId,
  uploadFileToPresignedUrlFx,
} from '@/features/upload-files'
import {
  type DeleteApiJingleCollectionsIdJinglesPathParams,
  type JingleCollectionInfoModel,
  type JingleModel,
  type MultiUploadJinglesRequestModel,
  postApiJingleCollectionsIdMultiUpload,
} from '@/shared/api'
import { notifyMaxLengthExceeded } from '@/shared/lib/form-utils'
import { showNotificationSuccessFx } from '@/shared/lib/providers'
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { previous } from 'patronum'

import {
  jingleAddToCollection,
  jingleCollectionJinglesOrderUpdate,
  jingleRemoveFromCollection,
} from '../api'
import {
  type JingleCollectionsIdJinglesQueryModelBase,
  createJingleCollectionsIdJinglesQueryModel,
} from '../lib/collection-id-jingles-query-model'
import { createJingleCollectionJinglesMultipleUploadModel } from '../lib/upload-multiple-jingle-files-to-jingle-collection'

// Upload jingles

const jinglesMultiUploadFx = createEffect(postApiJingleCollectionsIdMultiUpload)

export interface JingleFileForMultipleUpload {
  uid: string
  file: File
  params: MultiUploadJinglesRequestModel
}

export type UploadJingleMultipleFilesToJingleCollectionPayload = {
  jingleCollectionId: string
  jingles: JingleFileForMultipleUpload[]
  fileStartUpload: (payload: Pick<UploadFileWithId, 'uid' | 'id'>) => void
  fileUploadSuccess: (payload: Pick<UploadFileWithId, 'uid' | 'url'>) => void
  fileUploadError: (payload: Pick<UploadFileWithId, 'uid' | 'error'>) => void
}

export const uploadJingleMultipleFilesFx = createEffect(
  async ({
    jingleCollectionId,
    jingles,
    fileStartUpload,
    fileUploadSuccess,
    fileUploadError,
  }: UploadJingleMultipleFilesToJingleCollectionPayload) => {
    try {
      const data = jingles.map((jingle) => jingle.params)
      const { files } = await jinglesMultiUploadFx({
        id: jingleCollectionId,
        data,
      })
      if (!files || Object.keys(files).length !== jingles.length) {
        throw new Error(
          'Некорректный ответ от сервера: presigned URL не получены для всех файлов',
        )
      }
      await Promise.all(
        jingles.map((jingle) => {
          const { file, trackId: jingleId } = files[jingle.uid]
          fileStartUpload({ uid: jingle.uid, id: jingleId })

          return uploadFileToPresignedUrlFx({
            url: file.presignedUrl,
            file: jingle.file,
          })
            .then(() => {
              fileUploadSuccess({
                uid: jingle.uid,
                url: file.fileInfo.fullCdnUrl,
              })
            })
            .then(() => {
              void jingleByIdUploadStatus.start({ id: jingleId })
            })
            .catch((error) => {
              fileUploadError({ uid: jingle.uid, error })
              throw error
            })
        }),
      )
    } catch (error) {
      notifyMaxLengthExceeded(error)
      throw error
    }
  },
)

// Submit jingles on files upload

const jingleCollectionJingleMultiuploadGateOpened =
  createEvent<JingleCollectionInfoModel>()

const jingleCollectionJingleMultiuploadModel =
  createJingleCollectionJinglesMultipleUploadModel()

sample({
  clock: jingleCollectionByIdQuery.$data,
  filter: (jingleCollection): jingleCollection is JingleCollectionInfoModel =>
    jingleCollection !== null,
  target: jingleCollectionJingleMultiuploadGateOpened,
})

sample({
  clock: jingleCollectionJingleMultiuploadGateOpened,
  fn: (jingleCollection) => ({ jingleCollectionId: jingleCollection.id }),
  target: jingleCollectionJingleMultiuploadModel.Gate.open,
})

export const addJingleCollectionJingleMultiuploadFiles =
  jingleCollectionJingleMultiuploadModel.filesAdded

sample({
  source: jingleCollectionJingleMultiuploadModel.Gate.state,
  clock: jingleCollectionByIdQuery.reset,
  target: jingleCollectionJingleMultiuploadModel.Gate.close,
})
// Jingles

export const JingleCollectionsIdJinglesQueryModel =
  createJingleCollectionsIdJinglesQueryModel({
    initialFilters: {},
    usePagination: false,
    accumulate: false,
  }) as JingleCollectionsIdJinglesQueryModelBase
const {
  availableSet: jinglesJingleCollectionJinglesAvailableSet,
  unavailableSet: jinglesJingleCollectionJinglesUnavailableSet,
} = JingleCollectionsIdJinglesQueryModel
export const {
  // Query
  query: jinglesJingleCollectionJinglesQuery,
  // Collection Id
  collectionIdChanged: jinglesJingleCollectionJinglesJingleCollectionId,
  // Init
  filtersInit: jinglesJingleCollectionJinglesFiltersInit,
  // Refresh
  refreshed: jinglesJingleCollectionJinglesRefreshed,
} = JingleCollectionsIdJinglesQueryModel

sample({
  clock: jingleCollectionJingleMultiuploadModel.Gate.open,
  target: jinglesJingleCollectionJinglesAvailableSet,
})
sample({
  clock: jingleCollectionJingleMultiuploadModel.Gate.close,
  target: jinglesJingleCollectionJinglesUnavailableSet,
})

sample({
  source: jingleCollectionJingleMultiuploadModel.Gate.state,
  clock: jinglesJingleCollectionJinglesAvailableSet,
  fn: ({ jingleCollectionId }) => jingleCollectionId,
  target: jinglesJingleCollectionJinglesJingleCollectionId,
})

// Patch JingleCollection Jingles Status

export const $uploadStatusPatches = createStore<
  Record<
    JingleCollectionJingleWithStatus['id'],
    JingleCollectionJingleWithStatus['uploadStatus']
  >
>({})
  .on(jingleByIdUploadStatus.finished.success, (state, { params: { id } }) => ({
    ...state,
    [id]: TrackUploadStatus.Uploaded,
  }))
  .reset(jingleCollectionJingleMultiuploadModel.Gate.close)

// Reorder JingleCollection Jingles

export const jingleCollectionJinglesReordered =
  createEvent<JingleCollectionJingleWithStatus[]>()

const $jingleCollectionJinglesOrder = createStore<string[]>([])

$jingleCollectionJinglesOrder.on(
  jingleCollectionJinglesReordered,
  (_, newOrder) => newOrder.map((t) => t.id),
)

export const $jingleCollectionJinglesReorderBlocked = createStore(false)

sample({
  clock: jingleCollectionJinglesOrderUpdate.start,
  fn: () => true,
  target: $jingleCollectionJinglesReorderBlocked,
})

sample({
  clock: jingleCollectionJinglesOrderUpdate.finished.finally,
  fn: () => false,
  target: $jingleCollectionJinglesReorderBlocked,
})

sample({
  clock: jingleCollectionJinglesOrderUpdate.finished.success,
  fn: () => {
    return {
      message: 'Порядок джинглов обновлён',
    }
  },
  target: showNotificationSuccessFx,
})

// Revert jingles in jingleCollection local order on error
sample({
  clock: jingleCollectionJinglesOrderUpdate.finished.failure,
  source: previous($jingleCollectionJinglesOrder),
  filter: (order: string[] | null): order is string[] => order !== null,
  target: $jingleCollectionJinglesOrder,
})

// JingleCollection Jingles List
export type JingleCollectionJingleWithStatus = JingleModel & {
  fileUploadStatus: string
}
export const $jingleCollectionJinglesWithProgressReordered = combine(
  jinglesJingleCollectionJinglesQuery.$data,
  jingleCollectionJingleMultiuploadModel.$files,
  $jingleCollectionJinglesOrder,
  $uploadStatusPatches,
  (jingles, uploads, order, patches): JingleCollectionJingleWithStatus[] => {
    if (!jingles) return []
    const jinglesWithProgress: JingleCollectionJingleWithStatus[] =
      jingles.items.map((j) => {
        const upload = uploads.find((u) => u.id === j.trackId)
        const patchedUploadStatus = patches[j.trackId]

        return {
          ...j,
          key: j.id,
          fileUploadStatus: upload?.status ?? 'done',
          uploadStatus: patchedUploadStatus ?? j.uploadStatus,
        }
      })
    if (!order.length) return jinglesWithProgress
    const map = new Map(jinglesWithProgress.map((j) => [j.id, j]))
    const reordered = order
      .map((id) => map.get(id))
      .filter((jingle): jingle is JingleCollectionJingleWithStatus => !!jingle)
    const rest = jinglesWithProgress.filter((j) => !order.includes(j.id))

    return [...reordered, ...rest]
  },
)

// Remove jingle from collection

export const jingleRemovedFromCollection = createEvent<{
  bindId: DeleteApiJingleCollectionsIdJinglesPathParams['Id']
}>()

sample({
  source: jingleCollectionJingleMultiuploadModel.Gate.state,
  clock: jingleRemovedFromCollection,
  fn: ({ jingleCollectionId }, { bindId }) => ({
    id: jingleCollectionId,
    params: {
      BindIds: [bindId],
    },
  }),
  target: [jingleRemoveFromCollection.start],
})

sample({
  clock: jingleRemoveFromCollection.finished.success,
  fn: () => ({ message: `Джингл успешно убран из подборки` }),
  target: showNotificationSuccessFx,
})

sample({
  clock: jingleAddToCollection.finished.success,
  fn: () => ({ message: 'Джинглы успешно добавлены в подборку' }),
  target: showNotificationSuccessFx,
})

sample({
  clock: [
    jinglesMultiUploadFx.done,
    jingleUpdate.finished.success,
    jingleRemoveFromCollection.finished.success,
    jingleAddToCollection.finished.success,
    jingleCollectionJinglesOrderUpdate.finished.success,
    jingleCollectionJingleMultiuploadModel.fileUploadSuccess,
    uploadJingleMultipleFilesFx,
  ],
  target: jinglesJingleCollectionJinglesRefreshed,
})
